import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

const sectionContainer = css({
    width: "100%",
    backgroundColor: "#F4F5F7",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column"
});

const contentContainer = css({
    display: "flex",
    flexDirection: "row",
    maxWidth: "1168px",
    marginBlock: "128px",
    [media.below.lg]: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: "316px",
        padding: "0 10%",
        marginBlock: "40px"
    }
});

const textContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: "30px",
    alignItems: "flex-start",
    color: "#101214",
    paddingInlineStart: "30px",

    [media.below.lg]: {
        textAlign: "center",
        flexDirection: "column",
        alignItems: "center",
        minWidth: "316px",
        padding: "0 25px"
    }
});

const headingTextStyles = css({
    fontWeight: "700",
    fontSize: "3rem",
    lineHeight: 1.2,
    marginBlock: "0px",
    [media.below.lg]: {
        fontSize: "1.875rem"
    }
});

const ctaStyles = css({
    background: "none",
    fontSize: "1.25rem",
    appearance: "none",
    borderRadius: "30px",
    padding: "10px 30px",
    fontFamily: "inherit",
    border: "2px solid",
    cursor: "pointer"
});

const iconContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    flexBasis: "100%",
    gap: "16px 32px",
    justifyContent: "center",
    alignContent: "center",
    maxWidth: "584px",
    flexShrink: 0,

    [media.below.lg]: {
        width: "400px",
        marginBlockStart: "40px"
    }
});

const iconStyles = css({
    borderRadius: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animationIterationCount: 1,
    animationDuration: "0.75s",
    background: "#fff",
    width: "100px",
    height: "100px",
    padding: "20px",
    img: {
        maxWidth: "100%",
        maxHeight: "100%",
        width: "auto",
        height: "auto"
    },

    [media.below.lg]: {
        borderRadius: "15px",
        width: "64px",
        height: "64px",
        img: {
            maxWidth: "210%",
            maxHeight: "170%"
        }
    }
});

const animationStyle = css({
    "@keyframes hopup": {
        "0%": {
            transform: "translateY(0)"
        },
        "50%": {
            transform: "translateY(-15%)"
        },
        "100%": {
            transform: "translateY(0)"
        }
    },
    "animationName": "hopup"
});

export type iconDataProps = {
    readonly url: string;
    readonly altText: string;
};

export type offsetIconCollectionProps = {
    readonly iconData: iconDataProps[];
    readonly headingText: string;
    readonly bodyText: string;
    readonly ctaText: string;
    readonly ctaLink: string;
    readonly ctaOnClickFunc: () => void;
};

export const OffsetIconCollection = ({
    iconData,
    headingText,
    bodyText,
    ctaText,
    ctaLink,
    ctaOnClickFunc
}: offsetIconCollectionProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    return (
        <div css={sectionContainer}>
            <div css={contentContainer}>
                <div css={textContainerStyles}>
                    <h2 css={headingTextStyles}>{headingText}</h2>
                    <div>{bodyText}</div>
                    <a href={ctaLink} onClick={ctaOnClickFunc}>
                        <button css={ctaStyles}>{ctaText}</button>
                    </a>
                </div>
                <div css={iconContainerStyles} ref={scrollRef}>
                    {iconData.map((icon, index) => (
                        <div
                            key={index}
                            css={[
                                iconStyles,
                                sectionIsVisible ? animationStyle : null
                            ]}
                            style={{
                                animationDelay: `${0.5 + 0.25 * index}s`
                            }}
                        >
                            <img src={icon.url} alt={icon.altText} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
